import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Form, Button, Alert, Modal, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../Components/Layout/MainLayout/MainLayout';
import { Trash2 } from 'lucide-react';

const Settings = () => {
  const { tenantDomain } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState({
    tenantName: '',
    tenantDomain: ''
  });
  const [editedTenant, setEditedTenant] = useState({
    tenantName: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Delete tenant states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    const fetchTenantInfo = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/tenant`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include"
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        
        setTenant(data);
        setEditedTenant({ tenantName: data.tenantName });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching tenant information:', err);
        setError('Failed to load tenant information. Please try again later.');
        setLoading(false);
      }
    };

    fetchTenantInfo();
  }, [tenantDomain]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedTenant({ tenantName: tenant.tenantName });
    setError(null);
  };

  const handleChange = (e) => {
    setEditedTenant({ ...editedTenant, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      // Validate tenant name
      if (!editedTenant.tenantName.trim()) {
        setError('Tenant name cannot be empty');
        setIsSaving(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/tenant`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ tenantName: editedTenant.tenantName })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! Status: ${response.status}`);
      }

      // Update local state with the updated data
      setTenant({ ...tenant, tenantName: editedTenant.tenantName });
      setIsEditing(false);
      setSuccess('Tenant information updated successfully');
      setIsSaving(false);
    } catch (err) {
      console.error('Error updating tenant information:', err);
      setError(err.message || 'Failed to update tenant information. Please try again.');
      setIsSaving(false);
    }
  };
  
  const handleShowDeleteModal = () => {
    setShowDeleteModal(true);
    setDeleteConfirmation('');
    setDeleteError(null);
  };
  
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };
  
  const handleDeleteTenant = async () => {
    // Validate confirmation text matches tenant domain
    if (deleteConfirmation !== tenant.tenantDomain) {
      setDeleteError(`Please type "${tenant.tenantDomain}" to confirm tenant deletion`);
      return;
    }
    
    setIsDeleting(true);
    setDeleteError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/tenant/${tenantDomain}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include"
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete tenant");
      }
      
      // Clear local storage and redirect to install page
      localStorage.removeItem("tenantDomain");
      navigate('/install', { 
        state: { message: "Tenant has been deleted successfully" }
      });
    } catch (err) {
      console.error('Error deleting tenant:', err);
      setDeleteError(err.message || "An error occurred while trying to delete the tenant");
      setIsDeleting(false);
    }
  };

  return (
    <MainLayout>
      <Container className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Tenant Settings</h2>
            <p className="text-muted">View and manage your tenant settings</p>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger">{error}</Alert>
        )}

        {success && (
          <Alert variant="success">{success}</Alert>
        )}

        {loading ? (
          <div className="text-center py-5">Loading tenant information...</div>
        ) : (
          <>
            <Card className="mb-4">
              <Card.Header as="h5" className="d-flex justify-content-between align-items-center">
                <span>Tenant Information</span>
                {!isEditing ? (
                  <Button variant="primary" size="sm" onClick={handleEdit}>
                    Edit
                  </Button>
                ) : (
                  <div>
                    <Button variant="outline-secondary" size="sm" className="me-2" onClick={handleCancel} disabled={isSaving}>
                      Cancel
                    </Button>
                    <Button variant="success" size="sm" onClick={handleSave} disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                )}
              </Card.Header>
              <Card.Body>
                <Form>
                  <Form.Group as={Row} className="mb-3">
                    <Form.Label column sm={3}>Tenant Domain</Form.Label>
                    <Col sm={9}>
                      <Form.Control 
                        type="text" 
                        value={tenant.tenantDomain} 
                        readOnly
                      />
                    </Col>
                  </Form.Group>
                  
                  <Form.Group as={Row} className="mb-3">
                    <Form.Label column sm={3}>Tenant Name</Form.Label>
                    <Col sm={9}>
                      {isEditing ? (
                        <Form.Control 
                          type="text" 
                          name="tenantName"
                          value={editedTenant.tenantName} 
                          onChange={handleChange}
                        />
                      ) : (
                        <Form.Control 
                          type="text" 
                          value={tenant.tenantName} 
                          readOnly
                        />
                      )}
                    </Col>
                  </Form.Group>
                </Form>
              </Card.Body>
            </Card>
            
            <Card border="danger">
              <Card.Header as="h5" className="text-danger">Danger Zone</Card.Header>
              <Card.Body>
                <Row className="align-items-center">
                  <Col>
                    <h6 className="mb-0">Delete Tenant</h6>
                    <p className="text-muted mb-0">
                      Permanently delete this tenant and all associated data.
                    </p>
                  </Col>
                  <Col xs="auto">
                    <Button 
                      variant="danger" 
                      onClick={handleShowDeleteModal}
                    >
                      <Trash2 size={16} className="me-1" /> Delete Tenant
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </>
        )}
      </Container>
      
      {/* Delete Tenant Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Delete Tenant</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <p><strong>Warning:</strong> This action cannot be undone.</p>
            <p>Deleting this tenant will:</p>
            <ul>
              <li>Remove all user roles associated with this tenant</li>
              <li>Remove all tenant-user associations</li>
              <li>Remove all roles for this tenant</li>
              <li>Remove the tenant itself from the system</li>
            </ul>
            <p><strong>Note:</strong> User accounts will NOT be deleted, but users will lose access to this tenant.</p>
          </Alert>
          
          {deleteError && (
            <Alert variant="danger">
              {deleteError}
            </Alert>
          )}
          
          <Form.Group className="mb-3">
            <Form.Label>
              Type <strong>{tenant.tenantDomain}</strong> to confirm deletion:
            </Form.Label>
            <Form.Control
              type="text"
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              disabled={isDeleting}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteTenant} 
            disabled={isDeleting || deleteConfirmation !== tenant.tenantDomain}
          >
            {isDeleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              "Delete Tenant"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </MainLayout>
  );
};

export default Settings;